import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { ModalPasswordConfirmation, UiSelect } from 'components/common'
import { Button, Flex, FormControl, FormLabel, Grid, GridItem, Heading, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Textarea, useDisclosure, VStack } from '@chakra-ui/react'
import { createProjectProductionPO, deleteProjectProductionPO, getEmcVendors, getFabricationVendors, getProjectExtraDetailByProjectId, updateProjectProductionPO } from 'api'
import { useImmer } from 'use-immer'
import { useAppContext } from 'hooks/useAppContext'
import { getAssetUrl, PROJECT_PRODUCTION_PO_CATEGORIES } from 'lib'
import { format as formatDate } from 'date-fns'

const initialForm = {
  name: '',
  manualName: '',
  vendorCompany: '',
  vendorPhone: '',
  vendorEmail: '',
  vendorAddress: '',
  vendorCity: '',
  vendorState: '',
  vendorZip: '',
  emcModel: '',
  emcConfiguration: '',
  emcNominalSize: '',
  emcPitch: '',
  emcMatrix: '',
  emcCommunication: '',
  cabinetSeries: '',
  cabinetConfiguration: '',
  cabinetNominalSize: '',
  pedestalNominalSize: '',
  note: '',
  eta: null,
  price: 0
}

export const ModalProjectProductionPOType = {
  Add: 'Add',
  Edit: 'Edit'
}

export const ModalProjectProductionPO = ({ isOpen, onClose, type = ModalProjectProductionPOType.Add, po, projectId, reload }) => {
  const { createToast } = useAppContext()
  const [isLocked, setIsLocked] = useState(true)
  const [form, setForm] = useImmer(initialForm)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedVendor, setSelectedVendor] = useState(null)
  const disclosureRemovePO = useDisclosure()

  useEffect(() => {
    const load = async () => {
      try {
        const projectExtraDetail = await getProjectExtraDetailByProjectId(projectId)
        setForm(draft => {
          draft.emcModel = projectExtraDetail?.emcModel || ''
          draft.emcConfiguration = projectExtraDetail?.emcConfiguration || ''
          draft.emcNominalSize = projectExtraDetail?.emcNominalSize || ''
          draft.emcPitch = projectExtraDetail?.emcPitch || ''
          draft.emcMatrix = projectExtraDetail?.emcMatrix || ''
          draft.emcCommunication = projectExtraDetail?.emcCommunication || ''
          draft.cabinetSeries = projectExtraDetail?.cabinetSeries || ''
          draft.cabinetConfiguration = projectExtraDetail?.cabinetConfiguration || ''
          draft.cabinetNominalSize = projectExtraDetail?.cabinetNominalSize || ''
          draft.pedestalNominalSize = projectExtraDetail?.pedestalNominalSize || ''
        })
      } catch (err) {
        console.log(err.response.data)
      }
    }
    load()
  }, [])

  useEffect(() => {
    if (type === ModalProjectProductionPOType.Add || !po) {
      setForm(initialForm)
      setSelectedVendor(null)
    } else {
      setForm(draft => {
        Object.keys(po || {}).forEach(k => {
          if (k !== 'id' && k !== 'projectId' && k !== 'po' && k !== 'pdf' && k !== 'status' && k !== 'createdAt' && k !== 'updatedAt') {
            draft[k] = po[k] ?? ''
          }
        })
      })
      setSelectedVendor({
        name: po.vendorCompany || '',
        email: po.vendorEmail || '',
        phone: po.vendorPhone || '',
        address: po.vendorAddress || '',
        city: po.vendorCity || '',
        state: po.vendorState || '',
        zip: po.vendorZip || ''
      })
    }
  }, [type, po])

  const handleSave = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      let response
      if (type === ModalProjectProductionPOType.Add) {
        response = await createProjectProductionPO({ ...form, price: form.price * 1, projectId })
      } else {
        response = await updateProjectProductionPO(po.id, { ...form, price: form.price * 1 })
      }
      window.open(getAssetUrl(response.pdf))
      createToast('success', 'Success', 'Po saved.')
      setIsSaving(false)
      onClose()
      reload()
      setSelectedVendor(null)
      setForm(initialForm)
    } catch (err) {
      setIsSaving(false)
      createToast('error', 'Error', err.response.data)
    }
  }

  const handleRemove = async (id) => {
    try {
      await deleteProjectProductionPO(id)
      disclosureRemovePO.onClose()
      onClose()
      reload()
      createToast('success', 'Success', 'Po removed.')
    } catch {
      createToast('error', 'Error', 'Failed to remove the PO.')
    }
  }

  const handleChange = (e) => {
    setForm(draft => { draft[e.target.name] = e.target.value })
  }

  const eta = useMemo(() => {
    const date = new Date(form.eta)
    return form.eta ? formatDate(new Date(date.getTime() + date.getTimezoneOffset() * 60000), 'yyyy-MM-dd') : ''
  }, [form.eta])

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSave}>
            <ModalHeader>Purchase Order</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack alignItems="none" gap={4}>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Item Name</FormLabel>
                      <Select name="name" value={form.name} onChange={handleChange} placeholder="Select">
                        {Object.keys(PROJECT_PRODUCTION_PO_CATEGORIES).map(v =>
                          <option key={PROJECT_PRODUCTION_PO_CATEGORIES[v]} value={PROJECT_PRODUCTION_PO_CATEGORIES[v]}>{PROJECT_PRODUCTION_PO_CATEGORIES[v]}</option>
                        )}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Manual Name</FormLabel>
                      <Input type="text" name="manualName" value={form.manualName} onChange={handleChange} />
                    </FormControl>
                  </GridItem>
                </Grid>
                <Heading size="sm">Vendor</Heading>
                <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Company</FormLabel>
                      <UiSelect
                        value={selectedVendor}
                        onChange={v => {
                          setSelectedVendor(v)
                          setForm(draft => {
                            draft.vendorCompany = v?.name || ''
                            draft.vendorPhone = v?.phone || ''
                            draft.vendorEmail = v?.email || ''
                            draft.vendorAddress = v?.address || ''
                            draft.vendorCity = v?.city || ''
                            draft.vendorState = v?.state || ''
                            draft.vendorZip = v?.zip || ''
                          })
                        }}
                        loadData={(page, search) => {
                          if (form.name === PROJECT_PRODUCTION_PO_CATEGORIES.EMC) {
                            return getEmcVendors(page, 20, search, 'name', 'asc')
                          } else if (form.name === PROJECT_PRODUCTION_PO_CATEGORIES.Fabrication) {
                            return getFabricationVendors(page, 20, search, 'name', 'asc')
                          } else if (form.name === PROJECT_PRODUCTION_PO_CATEGORIES.Monument || form.name === PROJECT_PRODUCTION_PO_CATEGORIES.Steel) {
                            return Promise.all([
                              getEmcVendors(page, 20, search, 'name', 'asc'),
                              getFabricationVendors(page, 20, search, 'name', 'asc')
                            ])
                              .then(([respEmcVendors, respFabricationVendors]) => {
                                // Merge the results into a single array
                                const mergedResult = [...respEmcVendors.rows, ...respFabricationVendors.rows]
                                return { rows: mergedResult }
                              })
                          } else {
                            return []
                          }
                        }}
                        apiRespProp="rows"
                        getOptionValue={v => v.id}
                        getOptionLabel={v => v.name}
                        placeholder="Search Vendors"
                        noOptionsMessage={() => 'No Vendors Found'}
                        required={true}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Phone</FormLabel>
                      <Input type="text" name="vendorPhone" value={form.vendorPhone} onChange={handleChange} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Address</FormLabel>
                      <Input type="text" name="vendorAddress" value={form.vendorAddress} onChange={handleChange} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Email</FormLabel>
                      <Input type="text" name="vendorEmail" value={form.vendorEmail} onChange={handleChange} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>City</FormLabel>
                      <Input type="text" name="vendorCity" value={form.vendorCity} onChange={handleChange} />
                    </FormControl>
                  </GridItem>
                  <GridItem></GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>State</FormLabel>
                      <Input type="text" name="vendorState" value={form.vendorState} onChange={handleChange} />
                    </FormControl>
                  </GridItem>
                  <GridItem></GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Zip</FormLabel>
                      <Input type="text" name="vendorZip" value={form.vendorZip} onChange={handleChange} />
                    </FormControl>
                  </GridItem>
                </Grid>
                {form.name === PROJECT_PRODUCTION_PO_CATEGORIES.EMC && (
                  <>
                    <Heading size="sm">EMC</Heading>
                    <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Model</FormLabel>
                          <Input type="text" name="emcModel" value={form.emcModel} onChange={handleChange} isReadOnly={isLocked} />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Configuration</FormLabel>
                          <Input type="text" name="emcConfiguration" value={form.emcConfiguration} onChange={handleChange} isReadOnly={isLocked} />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Nominal Size</FormLabel>
                          <Input type="text" name="emcNominalSize" value={form.emcNominalSize} onChange={handleChange} isReadOnly={isLocked} />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Pitch</FormLabel>
                          <Input type="text" name="emcPitch" value={form.emcPitch} onChange={handleChange} isReadOnly={isLocked} />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Matrix</FormLabel>
                          <Input type="text" name="emcMatrix" value={form.emcMatrix} onChange={handleChange} isReadOnly={isLocked} />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Communication</FormLabel>
                          <Input type="text" name="emcCommunication" value={form.emcCommunication} onChange={handleChange} isReadOnly={isLocked} />
                        </FormControl>
                      </GridItem>
                    </Grid>
                    <Flex justifyContent="flex-end">
                      <Button onClick={() => setIsLocked(!isLocked)}>{`${isLocked ? 'Unlock' : 'Lock'} Inputs`}</Button>
                    </Flex>
                  </>
                )}
                {form.name === PROJECT_PRODUCTION_PO_CATEGORIES.Fabrication && (
                  <>
                    <Heading size="sm">Fabrication</Heading>
                    <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                      <GridItem>
                        <FormControl>
                          <FormLabel>ID Cabinet</FormLabel>
                          <Input type="text" name="cabinetSeries" value={form.cabinetSeries} onChange={handleChange} isReadOnly={isLocked} />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Cabinet Configuration</FormLabel>
                          <Input type="text" name="cabinetConfiguration" value={form.cabinetConfiguration} onChange={handleChange} isReadOnly={isLocked} />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Cabinet Nominal Size</FormLabel>
                          <Input type="text" name="cabinetNominalSize" value={form.cabinetNominalSize} onChange={handleChange} isReadOnly={isLocked} />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Pedestal Norminal Size</FormLabel>
                          <Input type="text" name="pedestalNominalSize" value={form.pedestalNominalSize} onChange={handleChange} isReadOnly={isLocked} />
                        </FormControl>
                      </GridItem>
                    </Grid>
                    <Flex justifyContent="flex-end">
                      <Button onClick={() => setIsLocked(!isLocked)}>{`${isLocked ? 'Unlock' : 'Lock'} Inputs`}</Button>
                    </Flex>
                  </>
                )}
                <Heading size="sm">Extra</Heading>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Price</FormLabel>
                      <Input
                        type="number"
                        value={form.price}
                        onChange={e => {
                          setForm(draft => {
                            draft.price = e.target.value
                          })
                        }}
                        onBlur={e => {
                          setForm(draft => {
                            draft.price = parseFloat(e.target.value || 0).toFixed(2)
                          })
                        }}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>ETA</FormLabel>
                      <Input type="date" name="eta" value={eta} onChange={handleChange} />
                    </FormControl>
                  </GridItem>
                </Grid>
                <FormControl>
                  <FormLabel>Notes</FormLabel>
                  <Textarea rows="5" name="note" value={form.note} onChange={handleChange} />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Flex justifyContent="flex-end" gap={4}>
                {type === ModalProjectProductionPOType.Edit && (
                  <Button type="button" colorScheme="red" onClick={() => disclosureRemovePO.onOpen()}>
                    Delete
                  </Button>
                )}
                <Button type="submit" colorScheme="blue" isLoading={isSaving}>
                  {type === ModalProjectProductionPOType.Add ? 'Add' : 'Save'}
                </Button>
                <Button onClick={onClose}>Close</Button>
              </Flex>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
      {disclosureRemovePO.isOpen && (
        <ModalPasswordConfirmation
          heading="Are you sure to remove the PO?"
          btnConfirmLabel="Remove"
          onConfirm={() => handleRemove(po.id)}
          {...disclosureRemovePO}
        />
      )}
    </>
  )
}

ModalProjectProductionPO.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  type: PropTypes.oneOf(Object.values(ModalProjectProductionPOType)),
  po: PropTypes.object,
  projectId: PropTypes.number,
  reload: PropTypes.func
}
